import React, { useEffect, useState } from 'react';
import { Button, Drawer, Space } from '@bedrock/components';
import classnames from 'classnames';
import classNames from 'classnames/bind';
import styles from './index.less';
import { ChevronLeft } from '@bedrock/icons-react';
import { SIDE_BAR_CLASS } from '#utils/constants';

const cx = classNames.bind(styles);

type ContainerDrawerProps = React.ComponentProps<typeof Drawer> & {
  header?: React.ReactNode;
  headerStyle?: React.CSSProperties;
  action?: React.ReactNode[];
  hiddenSearchBar?: boolean;
};

const ContainerDrawer: React.FC<ContainerDrawerProps> = (props) => {
  const {
    children,
    title,
    header,
    headerStyle,
    closable = true,
    action,
    hiddenSearchBar,
    ...restProps
  } = props;
  const [drawerWidth, setDrawerWidth] = useState<string | number>(0);
  const drawerRef = React.useRef<HTMLDivElement>(null);
  const resizeChange = (entries) => {
    const { width } = entries[0].contentRect;
    setDrawerWidth(`calc(100vw - ${width}px - 2px)`);
  };

  useEffect(() => {
    const menu = document.querySelector(SIDE_BAR_CLASS);
    if (!menu) {
      setDrawerWidth(`calc(100vw)`);
      return;
    }
    const resizeObserver = new ResizeObserver(resizeChange);
    resizeObserver.observe(menu);
    return () => {
      resizeObserver.unobserve(menu);
    };
  }, []);

  useEffect(() => {
    if (!restProps.visible) {
      return;
    }

    const removeShadow = () => {
      // 通过 drawerRef 查找当前组件实例的 drawer content
      if (!drawerRef.current) {
        return false;
      }

      // 查找当前 drawer 容器内的 rock-drawer-content
      const drawerContent = drawerRef.current
        .closest('.rock-drawer')
        ?.querySelector('.rock-drawer-content') as HTMLElement;
      if (drawerContent) {
        drawerContent.style.setProperty(
          'box-shadow',
          '0 0 0 0 transparent',
          'important'
        );
        return true;
      }

      return false;
    };

    // 使用 requestAnimationFrame 确保在下一帧执行
    let frameId: number;
    let timeoutId: ReturnType<typeof setTimeout>;
    let observer: MutationObserver | null = null;

    const tryRemoveShadow = () => {
      if (removeShadow()) {
        if (observer) {
          observer.disconnect();
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        return;
      }

      // 如果第一次没找到，使用 MutationObserver 监听 DOM 变化
      if (!observer) {
        observer = new MutationObserver(() => {
          if (removeShadow()) {
            observer?.disconnect();
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
          }
        });

        // 查找当前 drawer 的 body
        const drawerBody = drawerRef.current
          ?.closest('.rock-drawer')
          ?.querySelector('.rock-drawer-body');
        if (drawerBody) {
          observer.observe(drawerBody, {
            childList: true,
            subtree: true,
          });

          // 设置超时，避免无限监听
          timeoutId = setTimeout(() => {
            observer?.disconnect();
          }, 1000);
        }
      }
    };

    // 立即尝试一次
    tryRemoveShadow();

    // 使用 requestAnimationFrame 确保在渲染后执行
    frameId = requestAnimationFrame(() => {
      tryRemoveShadow();
    });

    // 延迟再尝试一次，确保 DOM 完全渲染
    const delayedTimeout = setTimeout(() => {
      tryRemoveShadow();
    }, 200);

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(delayedTimeout);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [restProps.visible]);

  return (
    <Drawer
      width={drawerWidth}
      zIndex={1000}
      showMask={false}
      bodyStyle={{ padding: 0, background: '#f5f5f5' }}
      {...restProps}
      className={classnames(cx('container-drawer-wrapper'), props.className, {
        [cx('hidden-search-bar')]: hiddenSearchBar,
      })}
      title={false}
      closable={false}
    >
      <div ref={drawerRef} style={{ display: 'contents' }}>
        {(closable || title || header) && (
          <div className={cx('container-drawer__header')} style={headerStyle}>
            <div className={cx('container-drawer__header-bar')}>
              <Space size={12}>
                {closable && (
                  <Button
                    type="checked-neutral"
                    icon={<ChevronLeft />}
                    onClick={props.onClose}
                  />
                )}
                {title && (
                  <span className={cx('container-drawer__header-bar-title')}>
                    {title}
                  </span>
                )}
              </Space>
              <Space size={12}>{action}</Space>
            </div>
            {header}
          </div>
        )}
        {children}
      </div>
    </Drawer>
  );
};

export default ContainerDrawer;
